/* Artwork-shaped spacing.

   Every uploaded artwork gets an "ink mask": a small grid over its trimmed
   artwork where a cell is ink if ANY pixel inside it has alpha at or above
   the trim threshold (so the mask never under-states the ink). Two copies
   are checked by the ink they actually print, not their rectangles: their
   transparent corners may overlap as long as the printed artwork keeps the
   minimum gap.

   The same code runs in the editor (live checks) and on the server (order
   checks), on the same masks, so both always agree. Distances are a lower
   bound: each mask cell counts as ink anywhere inside it, so a pair is never
   passed when the real ink is closer than the rule, and is at most about one
   cell more cautious than an exact measurement. Artwork with no mask (no
   transparency, or not analysed yet) counts as its full rectangle. */

import type { AssetInfo, Item } from './model.js';
import { itemBox, rectGap, type Rect } from './geometry.js';

/** Run-length encoded bitmap, base64 (see encodeInk). */
export interface InkMask { w: number; h: number; data: string }

/** Longest side of a mask, in cells. */
export const INK_MASK_MAX = 512;

/* ---------------------------------------------------------- encoding */

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function toB64(bytes: number[]): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i], b = bytes[i + 1], c = bytes[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    s += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + (b === undefined ? '=' : B64[(n >> 6) & 63]) + (c === undefined ? '=' : B64[n & 63]);
  }
  return s;
}
function fromB64(s: string): number[] {
  const out: number[] = [];
  const clean = s.replace(/[^A-Za-z0-9+/]/g, '');
  for (let i = 0; i < clean.length; i += 4) {
    const v = [0, 1, 2, 3].map(k => (i + k < clean.length ? B64.indexOf(clean[i + k]) : -1));
    const n = ((v[0] & 63) << 18) | ((v[1] & 63) << 12) | ((v[2] < 0 ? 0 : v[2]) << 6) | (v[3] < 0 ? 0 : v[3]);
    out.push((n >> 16) & 255);
    if (v[2] >= 0) out.push((n >> 8) & 255);
    if (v[3] >= 0) out.push(n & 255);
  }
  return out;
}

/** cells: one byte per cell (0 = clear, anything else = ink), row-major. */
export function encodeInk(cells: ArrayLike<number>, w: number, h: number): InkMask {
  const bytes: number[] = [];
  const put = (n: number) => { while (n >= 128) { bytes.push((n & 127) | 128); n = Math.floor(n / 128); } bytes.push(n); };
  let cur = 0, run = 0;
  for (let i = 0; i < w * h; i++) {
    const v = cells[i] ? 1 : 0;
    if (v === cur) run++;
    else { put(run); cur = v; run = 1; }
  }
  put(run);
  return { w, h, data: toB64(bytes) };
}

export function decodeInk(m: InkMask): Uint8Array {
  const cells = new Uint8Array(m.w * m.h);
  const bytes = fromB64(m.data);
  let i = 0, pos = 0, cur = 0;
  while (i < bytes.length && pos <= cells.length) {
    let n = 0, mul = 1, b: number;
    do { b = bytes[i++]; n += (b & 127) * mul; mul *= 128; } while (b & 128 && i < bytes.length);
    if (cur) cells.fill(1, pos, Math.min(cells.length, pos + n));
    pos += n;
    cur ^= 1;
  }
  return cells;
}

/**
 * Max-pool an alpha channel (one byte per pixel) into an ink mask: a cell is ink when any pixel it
 * covers is at or above `threshold`. `region` is the part of the alpha image to use (the trim box).
 */
export function inkFromAlpha(alpha: ArrayLike<number>, stride: number, region: { left: number; top: number; width: number; height: number }, threshold: number, max = INK_MASK_MAX): InkMask {
  const rw = Math.max(1, Math.round(region.width)), rh = Math.max(1, Math.round(region.height));
  const k = Math.max(rw, rh) > max ? max / Math.max(rw, rh) : 1;
  const w = Math.max(1, Math.round(rw * k)), h = Math.max(1, Math.round(rh * k));
  const cells = new Uint8Array(w * h);
  const t = Math.max(1, threshold | 0);
  const x0 = Math.round(region.left), y0 = Math.round(region.top);
  for (let y = 0; y < rh; y++) {
    const cy = Math.min(h - 1, Math.floor(y * h / rh));
    // a pixel that straddles two cells marks both
    const cy2 = Math.min(h - 1, Math.floor(((y + 1) * h - 1e-9) / rh));
    const row = (y0 + y) * stride + x0;
    for (let x = 0; x < rw; x++) {
      if (alpha[row + x] < t) continue;
      const cx = Math.min(w - 1, Math.floor(x * w / rw)), cx2 = Math.min(w - 1, Math.floor(((x + 1) * w - 1e-9) / rw));
      cells[cy * w + cx] = 1; cells[cy * w + cx2] = 1; cells[cy2 * w + cx] = 1; cells[cy2 * w + cx2] = 1;
    }
  }
  return encodeInk(cells, w, h);
}

/* ------------------------------------------------------------ shapes */

/** One resolution of a mask: its cells and the ink cells on the outline. */
interface Level { cells: Uint8Array; mw: number; mh: number; edge: Int32Array }

interface Shape {
  /** null = the whole rectangle is ink */
  cells: Uint8Array | null;
  mw: number; mh: number;
  /** ink cells on the outline (an empty or outside neighbour) */
  edge: Int32Array;
  /** the same mask max-pooled by 2, 4, 8 … (finest first); a copy is measured at the level that
      gives it cells of about 0.25 mm, which is plenty for gaps of a few millimetres */
  levels: Level[];
  /** where the mask sits in the item, as fractions of its width/height */
  fx: number; fy: number; fw: number; fh: number;
}

const shapeCache = new WeakMap<object, Map<string, Shape>>();
const FULL: Shape = { cells: null, mw: 1, mh: 1, edge: new Int32Array(0), levels: [], fx: 0, fy: 0, fw: 1, fh: 1 };

function outline(cells: Uint8Array, w: number, h: number): Int32Array {
  const edge: number[] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    if (!cells[i]) continue;
    if (x === 0 || y === 0 || x === w - 1 || y === h - 1 || !cells[i - 1] || !cells[i + 1] || !cells[i - w] || !cells[i + w]) edge.push(i);
  }
  return Int32Array.from(edge);
}

function pyramid(cells: Uint8Array, w: number, h: number): Level[] {
  const out: Level[] = [{ cells, mw: w, mh: h, edge: outline(cells, w, h) }];
  let c = cells, cw = w, ch = h;
  while (Math.max(cw, ch) > 16) {
    const nw = Math.ceil(cw / 2), nh = Math.ceil(ch / 2);
    const n = new Uint8Array(nw * nh);
    for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) if (c[y * cw + x]) n[(y >> 1) * nw + (x >> 1)] = 1;
    out.push({ cells: n, mw: nw, mh: nh, edge: outline(n, nw, nh) });
    c = n; cw = nw; ch = nh;
  }
  return out;
}

/** Cell size the distance check works at (mm): fine enough for a 3 mm rule, coarse enough to be quick. */
const TARGET_CELL_MM = 0.25;

/** The ink of an artwork as one copy of a design prints it (trimmed or the whole canvas). */
export function shapeFor(asset: (Pick<AssetInfo, 'trim' | 'opaque'> & { ink?: InkMask | null }) | undefined, trim: boolean): Shape {
  if (!asset || asset.opaque || !asset.ink) return FULL;
  let byTrim = shapeCache.get(asset);
  if (!byTrim) { byTrim = new Map(); shapeCache.set(asset, byTrim); }
  const key = trim ? 't' : 'f';
  const hit = byTrim.get(key);
  if (hit) return hit;
  const m = asset.ink;
  // the levels don't depend on trim: share them between the trimmed and untrimmed shapes
  const other = byTrim.get(trim ? 'f' : 't');
  const levels = other ? other.levels : pyramid(decodeInk(m), m.w, m.h);
  const t = asset.trim;
  const base = { cells: levels[0].cells, mw: m.w, mh: m.h, edge: levels[0].edge, levels };
  const s: Shape = trim ? { ...base, fx: 0, fy: 0, fw: 1, fh: 1 } : { ...base, fx: t.x, fy: t.y, fw: t.w, fh: t.h };
  byTrim.set(key, s);
  return s;
}

/** A copy on the sheet with its ink in sheet millimetres. */
interface Placed {
  box: Rect;
  /** outline points, x/y pairs (sheet mm) */
  pts: Float64Array;
  /** each point stands for ink anywhere within this radius (mm) */
  r: number;
  /** is this sheet point on (or within one cell of) ink? */
  inside: (x: number, y: number) => boolean;
}

const placedCache = new WeakMap<object, { shape: Shape; p: Placed }>();

function place(it: Item, shape: Shape): Placed {
  const hit = placedCache.get(it);
  if (hit && hit.shape === shape) return hit.p;
  const W = it.widthMm, H = it.heightMm;
  const a = it.rotation * Math.PI / 180, cos = Math.cos(a), sin = Math.sin(a);
  const sx = it.flipX ? -1 : 1, sy = it.flipY ? -1 : 1;
  // local (mm from the centre, before flip and rotation) → sheet: flip, then rotate (as the renderer does)
  const toSheet = (u: number, v: number): [number, number] => {
    const fu = u * sx, fv = v * sy;
    return [it.cx + fu * cos - fv * sin, it.cy + fu * sin + fv * cos];
  };
  const toLocal = (x: number, y: number): [number, number] => {
    const dx = x - it.cx, dy = y - it.cy;
    return [(dx * cos + dy * sin) * sx, (-dx * sin + dy * cos) * sy];
  };
  let pts: Float64Array, r: number, inside: Placed['inside'];
  if (!shape.cells) {
    // the full rectangle: its outline every half millimetre or so
    const step = Math.max(0.25, Math.min(0.5, Math.max(W, H) / 2000));
    const nx = Math.max(1, Math.ceil(W / step)), ny = Math.max(1, Math.ceil(H / step));
    const arr: number[] = [];
    for (let i = 0; i <= nx; i++) { const u = -W / 2 + W * i / nx; arr.push(...toSheet(u, -H / 2), ...toSheet(u, H / 2)); }
    for (let j = 1; j < ny; j++) { const v = -H / 2 + H * j / ny; arr.push(...toSheet(-W / 2, v), ...toSheet(W / 2, v)); }
    pts = Float64Array.from(arr);
    r = step / 2;
    inside = (x, y) => { const [u, v] = toLocal(x, y); return Math.abs(u) <= W / 2 + r && Math.abs(v) <= H / 2 + r; };
  } else {
    // the coarsest level whose cells are still at most ~0.25 mm on this copy (the finest if none is)
    let lv = shape.levels[0];
    for (const L of shape.levels) {
      if (Math.max(W * shape.fw / L.mw, H * shape.fh / L.mh) <= TARGET_CELL_MM) lv = L; else break;
    }
    const mw = lv.mw, mh = lv.mh, cells = lv.cells;
    const cw = W * shape.fw / mw, ch = H * shape.fh / mh;
    const ox = -W / 2 + W * shape.fx, oy = -H / 2 + H * shape.fy;
    pts = new Float64Array(lv.edge.length * 2);
    for (let k = 0; k < lv.edge.length; k++) {
      const i = lv.edge[k], cx = i % mw, cy = (i - cx) / mw;
      const [x, y] = toSheet(ox + (cx + 0.5) * cw, oy + (cy + 0.5) * ch);
      pts[2 * k] = x; pts[2 * k + 1] = y;
    }
    r = 0.5 * Math.hypot(cw, ch);
    inside = (x, y) => {
      const [u, v] = toLocal(x, y);
      const gx = Math.floor((u - ox) / cw), gy = Math.floor((v - oy) / ch);
      return gx >= 0 && gy >= 0 && gx < mw && gy < mh && cells[gy * mw + gx] === 1;
    };
  }
  const p: Placed = { box: itemBox(it), pts, r, inside };
  placedCache.set(it, { shape, p });
  return p;
}

export interface InkGap {
  /** the printed artwork overlaps */
  overlap: boolean;
  /** smallest ink-to-ink distance found (mm, a lower bound); Infinity when at least `limit` */
  distance: number;
  /** where they come closest (sheet mm), for a marker */
  at: { x: number; y: number } | null;
}

/* answers for pairs of (unchanged) copies: when one copy moves, only its pairs are measured again */
const pairCache = new WeakMap<object, WeakMap<object, { sa: Shape; sb: Shape; limit: number; g: InkGap }>>();

/** Ink-to-ink distance between two copies, looked for up to `limit` mm. */
export function inkGap(a: Item, sa: Shape, b: Item, sb: Shape, limit: number): InkGap {
  let row = pairCache.get(a);
  const hit = row?.get(b);
  if (hit && hit.sa === sa && hit.sb === sb && hit.limit === limit) return hit.g;
  const g = measure(a, sa, b, sb, limit);
  if (!row) { row = new WeakMap(); pairCache.set(a, row); }
  row.set(b, { sa, sb, limit, g });
  return g;
}

function measure(a: Item, sa: Shape, b: Item, sb: Shape, limit: number): InkGap {
  const A = place(a, sa), B = place(b, sb);
  const far: InkGap = { overlap: false, distance: Infinity, at: null };
  if (rectGap(A.box, B.box) >= limit) return far;
  const reach = limit + A.r + B.r;
  const near = (P: Placed, box: Rect) => {
    const out: number[] = [];
    for (let i = 0; i < P.pts.length; i += 2) {
      const x = P.pts[i], y = P.pts[i + 1];
      if (x >= box.x - reach && x <= box.x + box.w + reach && y >= box.y - reach && y <= box.y + box.h + reach) out.push(x, y);
    }
    return out;
  };
  const pa = near(A, B.box), pb = near(B, A.box);
  // ink on ink: outline points of one inside the other's ink (also catches one inside the other);
  // the marker goes in the middle of them
  let ox = 0, oy = 0, on = 0;
  for (let i = 0; i < pa.length; i += 2) if (B.inside(pa[i], pa[i + 1])) { ox += pa[i]; oy += pa[i + 1]; on++; }
  for (let i = 0; i < pb.length; i += 2) if (A.inside(pb[i], pb[i + 1])) { ox += pb[i]; oy += pb[i + 1]; on++; }
  if (on) return { overlap: true, distance: 0, at: { x: ox / on, y: oy / on } };
  if (!pa.length || !pb.length) return far;
  // nearest outline points, through a fine grid of B's points; each search only looks as far as the
  // best distance found so far, so it narrows quickly
  const rr = A.r + B.r;
  const cell = Math.max(0.5, Math.min(2, limit / 4));
  const grid = new Map<number, number[]>();
  const key = (gx: number, gy: number) => gx * 1048576 + gy;
  for (let i = 0; i < pb.length; i += 2) {
    const k = key(Math.floor(pb[i] / cell), Math.floor(pb[i + 1] / cell));
    const l = grid.get(k);
    if (l) l.push(i); else grid.set(k, [i]);
  }
  let best = Infinity, at: { x: number; y: number } | null = null;
  let bestC = (limit + rr) * (limit + rr);          // best centre-to-centre distance, squared
  for (let i = 0; i < pa.length; i += 2) {
    const x = pa[i], y = pa[i + 1];
    const gx = Math.floor(x / cell), gy = Math.floor(y / cell);
    const span = Math.ceil(Math.sqrt(bestC) / cell);
    for (let ox = -span; ox <= span; ox++) for (let oy = -span; oy <= span; oy++) {
      const l = grid.get(key(gx + ox, gy + oy));
      if (!l) continue;
      for (let q = 0; q < l.length; q++) {
        const j = l[q], dx = pb[j] - x, dy = pb[j + 1] - y, c = dx * dx + dy * dy;
        if (c < bestC) { bestC = c; best = Math.sqrt(c) - rr; at = { x: (x + pb[j]) / 2, y: (y + pb[j + 1]) / 2 }; }
      }
    }
  }
  if (best >= limit) return far;
  return { overlap: false, distance: Math.max(0, best), at };
}
